import { contentId } from "@lib/contentId";
import { matchingMvfScalesSchema } from "@lib/matchingMvf";
import { speculationFundingTypeSchema } from "@lib/specGrants/speculationFundingType";
import { mvfSchema } from "@lib/tranche";
import { trackSchema } from "@lib/types";
import { z } from "zod";
import { getActiveFunders, getActiveRecommenders } from "./accessors";

// ============================================================================
// Field Schemas and Types
// ============================================================================

export const fieldSourceSchema = z.enum([
  "longform",
  "googleform",
  "appInterface",
  "supplemental",
]);

export const fieldStatusSchema = z.enum(["active", "deleted"]);

const textQuestionSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("question"),
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

const sectionHeaderSchema = z.object({
  status: fieldStatusSchema,
  type: z.literal("sectionHeader"),
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  externalId: z.string().optional(),
  source: fieldSourceSchema,
});

export const fieldSchema = z.discriminatedUnion("type", [
  textQuestionSchema,
  sectionHeaderSchema,
]);

export type TextQuestion = z.infer<typeof textQuestionSchema>;
export type SectionHeader = z.infer<typeof sectionHeaderSchema>;
export type Field = z.infer<typeof fieldSchema>;
export type FieldSource = z.infer<typeof fieldSourceSchema>;
export type FieldStatus = z.infer<typeof fieldStatusSchema>;

// ============================================================================
// Typed Fields Schema and Types
// ============================================================================

export const keyIndividualSchema = z.object({
  name: z.string(),
  relationshipToProject: z.string(),
  contributionToProject: z.string(),
  contactInformation: z.string(),
});

export const typedFieldsSchema = z.object({
  contactInformation: z
    .object({ name: z.string(), email: z.string() })
    .optional(),
  matchRate: z
    .number()
    .optional()
    .describe("Amount the S-process provides for every external dollar"),

  maxStandardAmount: z
    .number()
    .optional()
    .describe("Maximum standard (unmatched) S-Process dollars"),

  ambitiousRequestAmount: z
    .number()
    .optional()
    .describe("Applicant's stretch/ambitious funding request"),
  baseRequestAmount: z.number().describe("Applicant's base funding request"),
  minGrantAmount: z
    .number()
    .optional()
    .describe("Minimum grant amount the applicant would accept"),
  maxGrantAmount: z
    .number()
    .optional()
    .describe("Maximum grant amount the applicant would accept"),
  receivingOrgName: z
    .string()
    .describe("Legal name of the organization receiving the grant"),
  receivingOrgTaxId: z
    .string()
    .describe("Tax ID (EIN) of the receiving organization"),
  receivingOrgLocation: z
    .string()
    .describe("Country or jurisdiction of the receiving organization"),
  sffApplicationId: z
    .string()
    .describe("Unique application ID from the SFF form"),
  longformLink: z
    .string()
    .describe("Link to the long-form application attachment"),

  targetsFreedom: z
    .boolean()
    .describe("Whether the proposal specifically pursues freedom"),
  targetsFairness: z
    .boolean()
    .describe("Whether the proposal specifically pursues fairness"),

  // Optional for older snapshots and rounds without Speculation Grant mappings.
  speculationPermissionToDiscuss: z
    .boolean()
    .optional()
    .describe("Speculation Grant permission to discuss"),
  speculationDescription: z
    .string()
    .optional()
    .describe("Speculation Grant activity description"),
  speculationRequestAmount: z
    .number()
    .nonnegative()
    .optional()
    .describe("Speculation Grant amount"),
  speculationReasonForExpedition: z
    .string()
    .optional()
    .describe("Reason for expedited funding"),
  speculationFundingType: speculationFundingTypeSchema
    .optional()
    .describe("Expedited funding or Matching Pledge?"),
  speculationWaysToReachYou: z
    .string()
    .optional()
    .describe("Speculation Grant contact options"),
  speculationEncouragement: z
    .string()
    .optional()
    .describe("Speculation Grantor who encouraged you"),

  keyIndividuals: z
    .array(keyIndividualSchema)
    .default([])
    .describe("Key individuals involved in the project (up to 4)"),
});

export type TypedFields = z.infer<typeof typedFieldsSchema>;

// ============================================================================
// Entity Type Schema and Type
// ============================================================================

export const entityTypeSchema = z.enum([
  "non-profit",
  "for-profit",
  "social-welfare",
]);

export type EntityType = z.infer<typeof entityTypeSchema>;

// ============================================================================
// Proposal Schemas and Types
// ============================================================================

export const applicationFormMappingSchema = z.object({
  formId: z.string(),
  fieldMapping: z.record(
    z.string(),
    z.union([z.string(), z.array(z.string())]),
  ),
  optionValues: z.record(z.string(), z.record(z.string(), z.string())),
});

export const proposalSchemaWithoutFieldResponses = z.object({
  id: z.string(),
  type: z.literal("proposal"),
  status: z.enum(["active", "withdrawn"]),
  entityType: entityTypeSchema,
  // (i.e. precise timestamp of Google Form submission)
  externalId: z.string().optional(),
  applicationSyncIssue: z.string().optional(),
  applicationDeleted: z.literal(true).optional(),
  applicationMapping: applicationFormMappingSchema.optional(),
  shortname: z.string(),
  longname: z.string(),
  firstSubmittedDatetime: z.string(),
  lastSubmittedDatetime: z.string(),
  requestsMatchingPledge: z.boolean(),
  typedFields: typedFieldsSchema,
});

export const proposalWithFieldResponsesIdSchema =
  proposalSchemaWithoutFieldResponses.extend({
    fieldResponsesId: z.string(),
  });

export const proposalWithFieldResponsesSchema =
  proposalSchemaWithoutFieldResponses.extend({
    fieldResponses: z.record(z.string(), z.string()),
  });

export type Proposal = z.infer<typeof proposalSchemaWithoutFieldResponses>;
export type ProposalWithFieldResponsesId = z.infer<
  typeof proposalWithFieldResponsesIdSchema
>;
export type ProposalWithFieldResponses = z.infer<
  typeof proposalWithFieldResponsesSchema
>;

// Alias exports for backwards compatibility
export const proposalWithFieldResponsesId = proposalWithFieldResponsesIdSchema;
export const proposalWithFieldResponses = proposalWithFieldResponsesSchema;
export const entityType = entityTypeSchema;

// ============================================================================
// Track Status Schema and Types
// ============================================================================

export const trackStatusSchema = z.enum([
  "1.onboarding", // only show recusals/investigation interest
  "2.initial-evaluations", // recommenders can only see proposals and their own MVF & notes, not others'
  "3.full-discussion", // see alluvial chart, disagreements, others' MVFs; everything
  "4.evals-freeze", // recommenders can see everything still but can't change standard MVFs; matching parameters, notes, and feedback stay editable
  "5.complete", // everything is in read-only mode
]);

export type TrackStatus = z.infer<typeof trackStatusSchema>;

const evaluationStateSchema = z.enum([
  "not-started",
  "evaluated",
  "canceled",
  "recused",
  "deferred",
]);

const recusalVoteSchema = z.enum(["y", "n", "a"]);

// A recommender's final verdict on an evaluable they are done with but have
// not meaningfully evaluated. Deliberately not a new `evaluationStateSchema`
// member: that enum is a lifecycle state machine, and a verdict is an
// orthogonal annotation that can coexist with `not-started` or a zeroed
// `evaluated`.
export const evaluationFinalitySchema = z.enum([
  "not-evaluated", // "I haven't done a meaningful evaluation of this proposal"
  "negligible-value", // "I think this proposal is of zero or negligible value"
  "negative-value", // "I think this proposal is of negative value"
]);

export type EvaluationFinality = z.infer<typeof evaluationFinalitySchema>;

const evaluationSchema = z.object({
  state: evaluationStateSchema,
  deferredTo: z.string().optional(),
  evaluableType: z.enum(["proposal", "recommender"]),
  evaluableId: z.string(),
  setToZero: z.boolean().optional(),
  hidden: z.boolean().optional(),
  lastTouchedAt: z.string().optional(),
  // Optional forever: evaluations serialized before this field existed (DB
  // snapshots, the client offline cache, committed fixtures) have no value,
  // and "no verdict recorded" is exactly what a missing value means.
  // Anonymization: deliberately visible to all viewers/tracks, like `state`
  // and `setToZero` — a closed enum with no free text, and funders seeing it
  // is the point of the feature.
  finality: evaluationFinalitySchema.optional(),
  // The recommender's standing claim that this evaluable is actively harmful.
  // Independent of the curve by construction, not by policy: an MVF bottoms
  // out at zero, so "negative" is the one thing it cannot say. Absent means
  // no claim, never "they said it isn't harmful". Anonymization: visible to
  // all viewers like `state` and `setToZero` — a bare boolean with no free
  // text, and funders seeing it is the point.
  negativeValue: z.boolean().optional(),
});

export const evaluationFeedbackPositiveNegativeSchema = z.object({
  positive: z.string(),
  negative: z.string(),
});

export const evaluationFeedbackTypeSchema =
  evaluationFeedbackPositiveNegativeSchema;

const evaluableFeedbackSchema = z.object({
  positiveNegative: evaluationFeedbackPositiveNegativeSchema,
});

// A round's reading of an Application Builder form: which question answers
// each round field (`roundFields.ts`) and, for choice fields, what each option
// means. Keyed by the form's permanent question and option ids, so republishing
// the form never moves a mapping; a deleted question leaves a dangling id the
// Proposal Forms page reports rather than a silent gap. Keys are plain strings
// (not the round-field enum) so a field retired from the registry still
// replays; readers ignore ids they don't know.
//
// Option values hang off the FIELD, not the question: they are what the
// field's values look like on that question, so re-pointing the field to
// another question drops them (the reducer clears them), and two choice
// fields reading one question can't share each other's values.
export const applicationFormAttachmentSchema = z.object({
  formId: z.string(),
  // roundFieldId -> questionId. Only mapped fields are present.
  fieldMapping: z.record(z.string(), z.string()),
  // roundFieldId -> optionId (or OTHER_OPTION_ID) -> one of that field's values.
  optionValues: z.record(z.string(), z.record(z.string(), z.string())),
});

// The key under `optionValues` for a choice question's free-text "Other"
// answer, which has no option id of its own.
export const OTHER_OPTION_ID = "__other__";

export type ApplicationFormAttachment = z.infer<
  typeof applicationFormAttachmentSchema
>;

const proposalFormSchema = z.object({
  status: z.enum(["active", "deleted"]),
  id: z.string(),
  name: z.string(),
  googleFormId: z.string(),
  spreadsheetId: z.string(),
  spreadsheetRange: z.string(),
  // The value matched against the rolling application spreadsheet's Round
  // column when this form syncs. It keys into a spreadsheet, so it belongs to
  // the form that names the spreadsheet — not to the round. Optional because
  // forms added before it existed rely on the round-level legacy value; read
  // it through `getProposalFormRoundIdentifier`, never directly.
  roundIdentifier: z.string().optional(),
  fields: z.array(fieldSchema),
  proposals: z.record(z.string(), proposalWithFieldResponsesIdSchema),
  // Present iff the form's proposals come from an Application Builder form
  // (ATTACH_APPLICATION_FORM) rather than a spreadsheet; such forms have
  // empty Google ids, like the round's default form. Optional so snapshots,
  // the offline cache and fixtures from before it existed still parse.
  applicationForm: applicationFormAttachmentSchema.optional(),
  // Source identity survives detachment so reattachment reuses proposal history.
  applicationFormSourceId: z.string().optional(),
});

const incrementalEvalStepSchema = z.object({
  evaluableId: z.string(),
  incrementAmount: z.number(),
});

const recommenderSchema = z.object({
  id: z.string(),
  memberId: z.string(),
  type: z.literal("recommender"),
  status: z.enum(["active", "removed"]),
  shortname: z.string(),
  evaluations: z.record(z.string(), evaluationSchema),
  mvfProfile: z.record(z.string(), mvfSchema),
  matchingMvfScales: z.record(z.string(), matchingMvfScalesSchema),
  meanRecommenderThreshold: z.number().optional(),
  mvfChanges: z.number(),
});

export const DEFAULT_FUNDER_MATCHING_BUDGET_RATIO = 0.25;

const funderSchema = z.object({
  id: z.string(),
  memberId: z.string(),
  type: z.literal("funder"),
  status: z.enum(["active", "removed"]),
  shortname: z.string(),
  budget: z.number(),
  speculationGrantSettlementBudget: z.number().nullable(),
  scores: z.record(
    z.string(),
    z.record(z.string(), z.number().nullable().optional()),
  ),
  proposalWeights: z.record(z.string(), z.number()).default({}),
  recommenderWeights: z.record(z.string(), z.number()).default({}),
  excludedProposalIds: z.array(z.string()).default([]),
  // Optional so funders serialized before matching could be disabled still
  // parse. Missing remains enabled to preserve historical round behavior;
  // newly created funders persist false explicitly.
  usesMatching: z.boolean().optional(),
  // Optional so funders serialized before per-funder ratios still parse.
  // Accessors use DEFAULT_FUNDER_MATCHING_BUDGET_RATIO when this is absent.
  matchingBudgetRatio: z.number().min(0).max(1).optional(),
  incrementalEvalSteps: z.array(incrementalEvalStepSchema),
  undoneIncrementalEvalSteps: z.array(incrementalEvalStepSchema),
});

const evaluatorSchema = z.discriminatedUnion("type", [
  recommenderSchema,
  funderSchema,
]);

const roundMemberSchema = z.object({
  status: z.enum(["active", "removed"]),
  userId: z.string(),
  id: z.string(),
  isRoundAdmin: z.boolean(),
  initials: z.string(),
  track: trackSchema,
  createdAt: z.string(),
  feedbackVisibleByDefault: z.boolean(),
  deanonymize: z.boolean(),
  evaluableFeedback: z.record(z.string(), evaluableFeedbackSchema),
  evaluableNotes: z.record(z.string(), z.string()),
  // Per-round note the recommender writes for the funder (SP-2152). Optional so
  // existing snapshots and member literals parse without a migration.
  generalNotes: z.string().optional(),
  // Restricted funder view config: a funder who must not see videos,
  // transcripts, AI syntheses, or the hidden recommenders' notes. Optional so
  // existing snapshots and member literals parse without a migration.
  isRestrictedFunder: z.boolean().optional(),
  hiddenRecommenderMemberIds: z.array(z.string()).optional(),
  evaluablePermissions: z.record(
    z.string(),
    z.enum(["default", "visible", "hidden"]),
  ),
  evaluators: z.record(z.string(), evaluatorSchema),
});

const disclosureSchema = z.object({
  disclosure: z.string(),
  selfRecused: z.boolean(),
  votes: z.record(z.string(), recusalVoteSchema),
  decision: z.union([z.literal("recused"), z.literal("not-recused"), z.null()]),
  hasDisclosure: z.boolean(),
});

const trackSettingsSchema = z.object({
  status: trackStatusSchema,
  evaluationsDueDate: z.string().optional(),
});

const nodeSchema = z.union([
  recommenderSchema,
  funderSchema,
  proposalWithFieldResponsesIdSchema,
]);

export const roundApplicationAudienceSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("public") }),
  z.object({ type: z.literal("admins") }),
  z.object({ type: z.literal("link") }),
  z.object({
    type: z.literal("people"),
    userIds: z.array(z.string().startsWith("u-")).max(1000),
  }),
]);
export type RoundApplicationAudience = z.infer<
  typeof roundApplicationAudienceSchema
>;

// Optional bounds preserve the availability of rounds created before scheduling.
export const roundApplicationWindowSchema = z
  .object({
    opensAt: z.string().datetime().nullable(),
    closesAt: z.string().datetime().nullable(),
  })
  .refine(
    ({ opensAt, closesAt }) =>
      opensAt == null ||
      closesAt == null ||
      Date.parse(opensAt) < Date.parse(closesAt),
    { message: "Closing time must be after opening time." },
  );

export const roundAggregateSchema = z.object({
  type: z.literal("Round"),
  version: z.number(),
  id: z.string(),
  latestActionId: z.string(),
  status: z.enum(["active", "deleted"]),
  // ISO timestamp of when the round was archived; absent while it is live.
  // An archived round is read-only in the engine (see isActionPermitted) and
  // hidden from the everyday round lists. Optional for backward
  // compatibility: snapshots, the offline cache, and committed fixtures that
  // predate archiving have no value, which reads as "not archived".
  archivedAt: z.string().optional(),
  // Legacy snapshots omit this field. Applications stay closed until an admin
  // explicitly opens them, independently of the round's evaluation phase.
  applicationsOpen: z.boolean().optional(),
  applicationWindow: roundApplicationWindowSchema.optional(),
  // Existing open rounds were public. Retain the audience when closing/reopening.
  // Like other round settings, opaque audience IDs are visible to authorized
  // round members. Contact identities are fetched separately by admins only.
  applicationAudience: roundApplicationAudienceSchema.optional(),
  isSfcRound: z.boolean(),
  // Legacy: rounds created before the identifier moved onto the proposal form
  // (CREATE_ROUND v3) carry it here, and `getProposalFormRoundIdentifier`
  // falls back to it for forms that have none of their own. New rounds never
  // set it.
  roundIdentifier: z.string().optional(),
  advancedAnalyticsEnabled: z.boolean(),
  // Replay-only state for the deprecated round-wide matching action. This is
  // distinct from the removed matchingBudgetRatio snapshot key: it lets a
  // historical funder added after that action inherit the action's value.
  deprecatedMatchingAdjustmentIntensity: z.number().min(0).max(1).optional(),
  // When false, the round does not use tracks: every ordinary recommender sits in the
  // MAIN track and track-related UI is hidden. We model "no tracks" as a single
  // real track (MAIN) plus this flag rather than via the NO_TRACK sentinel,
  // because NO_TRACK already means "unassigned / cross-cutting member who sees
  // every track" — overloading it to also mean "this round has no tracks" would
  // conflate those two ideas (and the engine deliberately excludes NO_TRACK from
  // phase gating, recusal eligibility, and track summaries). The synthetic Mean
  // member represents all tracks and always defaults to NO_TRACK.
  //
  // Optional for backward compatibility: rounds (and committed fixtures)
  // serialized before this flag existed have no value, and `roundUsesTracks`
  // treats a missing flag as `true`. New rounds always persist it explicitly.
  usesTracks: z.boolean().optional(),
  // Legacy snapshots retain their settings; new creation events record false.
  allowsAnonymization: z.boolean().optional(),
  name: z.string(),
  // Round context shared with applicants; absent in historical snapshots.
  description: z.string().optional(),
  holdProposalId: z.string(),
  members: z.record(z.string(), roundMemberSchema),
  proposalForms: z.record(z.string(), proposalFormSchema),
  proposalMemberAxes: z.object({
    proposalIds: z.array(z.string()),
    memberIds: z.array(z.string()),
  }),
  investigation: z.array(z.array(z.number())),
  reviewProgress: z.array(z.array(z.number())),
  proposalDisclosure: z.record(
    z.string(),
    z.record(z.string(), disclosureSchema),
  ),
  trackSettings: z.object({
    MAIN: trackSettingsSchema,
    FAIRNESS: trackSettingsSchema,
    FREEDOM: trackSettingsSchema,
  }),
});

export type TrackSettings = z.infer<typeof trackSettingsSchema>;
export type ProposalForm = z.infer<typeof proposalFormSchema>;
export type Disclosure = z.infer<typeof disclosureSchema>;
export type EvaluationState = z.infer<typeof evaluationStateSchema>;
export type IncrementalEvalStep = z.infer<typeof incrementalEvalStepSchema>;
export type Recommender = z.infer<typeof recommenderSchema>;
export type Funder = z.infer<typeof funderSchema>;
export type RoundMember = z.infer<typeof roundMemberSchema>;
export type Evaluation = z.infer<typeof evaluationSchema>;
export type Node = z.infer<typeof nodeSchema>;
export type RoundAggregate = z.infer<typeof roundAggregateSchema>;
export type RoundMemberRecusal = {
  evaluableType: z.infer<typeof evaulationItemTypeSchema>;
  evaluableId: string;
};

// ── Algorithm input parts ────────────────────────────────────────────────────
//
// The identities of exactly the aggregate subtrees the S-Process allocation
// reads, split by which side of the computation they feed. Because the
// reducer evolves aggregates with structural sharing (mutative), a subtree an
// action didn't touch keeps its reference — so element-wise identity
// comparison of these lists answers "could this input have changed?" without
// looking at content.
//
// Two consumers, one input-surface definition:
//   - roundsCouldDifferInResult (below) compares both lists across two rounds
//     to gate recomputation. False positives allowed, false negatives not.
//   - The per-round algorithm caches (MVF/tranche profiles, recommender-layer
//     scaffold, whitelists — accessors.ts and runSProcessOnRound.ts) validate
//     against the RECOMMENDER parts instead of the round identity, so caches
//     survive actions that only touch funder state (a Decide push, a rating)
//     or nothing algorithm-relevant at all (a note edit).
//
// If a new input reaches the allocation, it must be added HERE — same failure
// mode as the LLM inputVersion hashes: an input that reaches the computation
// but not the key means stale results served forever.

// Everything the recommender layer reads: the active recommender set with
// each recommender's whole evaluator object — the algorithm inputs
// (evaluations gate MVF inclusion/deferrals, MVF curves, matching scales,
// mean threshold) AND the display fields (shortname) that ride along in the
// cached ordered-recommender lists into layerNodes, warnings, and the Decide
// matrix headers. Using the object identity rather than picked fields means
// any change to a recommender — including fields added later — flushes,
// while funder/member/note churn still leaves the parts untouched. Plus
// proposalForms (active-proposal set, request caps, matchRates scale the
// matching curves) and the hold proposal.
//
// Memoized per aggregate instance: the caches validate against these parts on
// every computation, and the parts of a given (immutable) aggregate never
// change.
const recommenderAlgoInputPartsCache = new WeakMap<RoundAggregate, unknown[]>();
export function getRecommenderAlgoInputParts(round: RoundAggregate): unknown[] {
  const cached = recommenderAlgoInputPartsCache.get(round);
  if (cached) return cached;
  const parts: unknown[] = [round.holdProposalId, round.proposalForms];
  for (const recommender of getActiveRecommenders(round)) {
    parts.push(recommender);
  }
  recommenderAlgoInputPartsCache.set(round, parts);
  return parts;
}

// Everything the funder layer reads per funder. Unlike the recommender parts,
// these are picked fields rather than the funder object: scores change on
// every rating — a hot, allocation-irrelevant action that must not flush
// caches or trigger recomputes. (speculationGrantSettlementBudget and
// evaluations can't affect output either. Funder shortnames appear only in
// remaining-budget warnings, an accepted staleness for this trade.)
const funderAlgoInputPartsCache = new WeakMap<RoundAggregate, unknown[]>();
export function getFunderAlgoInputParts(round: RoundAggregate): unknown[] {
  const cached = funderAlgoInputPartsCache.get(round);
  if (cached) return cached;
  const parts: unknown[] = [];
  for (const funder of getActiveFunders(round)) {
    parts.push(
      funder.id,
      funder.budget,
      funder.usesMatching,
      funder.matchingBudgetRatio,
      funder.incrementalEvalSteps,
      funder.excludedProposalIds,
    );
  }
  funderAlgoInputPartsCache.set(round, parts);
  return parts;
}

export function algoInputPartsEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// Could these rounds have different S-Process outputs?  Note that because the
// round aggregate is immutable, we can compare data structures by reference.
// It's possible this will lead to false positives, where we return true even if
// they have the same result, but we can't have false negatives (which is the
// important thing).
export function roundsCouldDifferInResult(
  round1: RoundAggregate,
  round2: RoundAggregate,
): boolean {
  return (
    !algoInputPartsEqual(
      getRecommenderAlgoInputParts(round1),
      getRecommenderAlgoInputParts(round2),
    ) ||
    !algoInputPartsEqual(
      getFunderAlgoInputParts(round1),
      getFunderAlgoInputParts(round2),
    )
  );
}

export type PositiveNegativeFeedback = z.infer<
  typeof evaluationFeedbackPositiveNegativeSchema
>;

export type EvaluableFeedback = z.infer<typeof evaluationFeedbackTypeSchema>;

export const evaulationItemTypeSchema = z.enum(["proposal", "recommender"]);
export type EvaluableType = z.infer<typeof evaulationItemTypeSchema>;

export const evaluatorTypeSchema = z.enum(["recommender", "funder"]);

export function getFieldResponsesId(fieldResponses: {
  [fieldId: string]: string;
}): string {
  return contentId(fieldResponses);
}
